import { useInstantFormat } from '../instant-format';
/**
 * iCalendar dialogs of a calendar (Outlook "Import", "Publish"): import an .ics file into an own calendar, and
 * publish a calendar as a read-only link (full details or busy times only; copy, stop publishing).
 */
import { useI18n } from '@platform/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, ErrorState, Input, Label, SegmentedControl, Skeleton, toast, useAsync, useErrorText } from '@platform/ui';
import { CopyIcon, Trash2Icon } from 'lucide-react';
import { useId, useState } from 'react';
import { api, apiPublicUrl, type CalendarInfo } from '../api';
import { useCalendar } from './calendar-context';
import { calendarName } from './events';

export function ImportDialog({ calendar, onClose }: { calendar: CalendarInfo; onClose: () => void }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { changed, zone } = useCalendar();
  const fileId = useId();
  const [file, setFile] = useState<File>();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const result = await api.importIcs(calendar.id, await file.text(), zone.zone);
      toast.success(t('webmail.calendarUx.ics.importedToast', { created: result.created, failed: result.failed + result.skipped }));
      changed();
      onClose();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendarUx.ics.importTitle')}</DialogTitle>
          <DialogDescription>{t('webmail.calendarUx.ics.importDescription', { name: calendarName(calendar, t) })}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={fileId}>{t('webmail.calendarUx.ics.file')}</Label>
          <Input id={fileId} type="file" accept=".ics,text/calendar" onChange={(e) => setFile(e.target.files?.[0])} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void run()} disabled={busy || !file}>
            {t('webmail.calendarUx.ics.import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PublishDialog({ calendar, onClose }: { calendar: CalendarInfo; onClose: () => void }) {
  const { t } = useI18n();
  const formatDate = useInstantFormat();
  const errorText = useErrorText();
  const list = useAsync(() => api.calendarPublications());
  const [detail, setDetail] = useState<'full' | 'busy'>('busy');
  const [busy, setBusy] = useState(false);
  const mine = (list.data ?? []).filter((p) => p.calendarId === calendar.id);
  const link = (path: string) => apiPublicUrl(path);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('webmail.calendarUx.ics.copiedToast'));
    } catch {
      toast.error(t('webmail.calendarUx.ics.copyFailed'));
    }
  };
  const act = async (run: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await run();
      list.reload();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendarUx.ics.publishTitle', { name: calendarName(calendar, t) })}</DialogTitle>
          <DialogDescription>{t('webmail.calendarUx.ics.publishDescription')}</DialogDescription>
        </DialogHeader>
        {list.error ? (
          <ErrorState description={errorText.describe(list.error)} onRetry={list.reload} />
        ) : !list.data ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <div className="flex flex-col gap-3">
            {mine.map((p) => (
              <div key={p.token} className="flex flex-col gap-2 rounded-md border p-2">
                <span className="text-xs text-muted-foreground">
                  {t(p.detail === 'full' ? 'webmail.calendarUx.ics.detailFull' : 'webmail.calendarUx.ics.detailBusy')} · {t('webmail.calendarUx.ics.updated', { time: formatDate(p.refreshedAt) })}
                </span>
                <div className="flex gap-2">
                  <Input readOnly value={link(p.path)} aria-label={t('webmail.calendarUx.ics.link')} onFocus={(e) => e.currentTarget.select()} />
                  <Button size="icon" variant="outline" aria-label={t('webmail.calendarUx.ics.copy')} onClick={() => void copy(link(p.path))}>
                    <CopyIcon />
                  </Button>
                  <Button size="icon" variant="outline" aria-label={t('webmail.calendarUx.ics.unpublish')} disabled={busy} onClick={() => void act(() => api.unpublishCalendar(p.token))}>
                    <Trash2Icon />
                  </Button>
                </div>
              </div>
            ))}
            <SegmentedControl<'full' | 'busy'>
              label={t('webmail.calendarUx.ics.detail')}
              value={detail}
              options={[
                { value: 'busy', label: t('webmail.calendarUx.ics.detailBusy') },
                { value: 'full', label: t('webmail.calendarUx.ics.detailFull') },
              ]}
              onChange={setDetail}
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('webmail.calendarUx.settings.done')}
          </Button>
          <Button disabled={busy || !list.data} onClick={() => void act(() => api.publishCalendar({ calendar: calendar.id, detail }))}>
            {t('webmail.calendarUx.ics.publish')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
